import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileText, Upload, Loader2, Save, Trash2, ExternalLink } from "lucide-react";
import { api, mediaUrl, formatApiError } from "@/lib/api";
import { useContent } from "@/context/ContentContext";

export const LEGAL_DOCS = [
  { key: "cgv_traiteur", title: "CGV Traiteur", path: "/cgv-traiteur" },
  { key: "cgv_coordination", title: "CGV Coordination événementielle", path: "/cgv-coordination" },
  { key: "cgv_chef", title: "Conditions Cheffe à domicile", path: "/cgv-cheffe" },
  { key: "mentions", title: "Mentions légales", path: "/mentions-legales" },
];

const btn = "inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium transition-colors disabled:opacity-50";

function DocCard({ doc, summary, file, onSaved }) {
  const [text, setText] = useState(summary);
  const [pdf, setPdf] = useState(file || null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setText(summary); setPdf(file || null); }, [summary, file]);
  const dirty = text !== summary || (pdf?.url || null) !== (file?.url || null);
  const id = doc.key;

  const upload = async (f) => {
    if (!f) return;
    if (f.type !== "application/pdf") { toast.error("Veuillez choisir un fichier PDF"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPdf({ url: r.data.url, name: f.name });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Envoi impossible"); }
    finally { setBusy(false); }
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/content/legal-doc", { key: id, summary: text, file_url: pdf?.url || null, file_name: pdf?.name || null });
      await onSaved();
      toast.success(`${doc.title} enregistré`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Enregistrement impossible"); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-[#2C2C2C]/10 bg-white p-6" data-testid={`legal-doc-${id}`}>
      <div className="flex items-center justify-between gap-3">
        <h2 className="font-serif text-2xl text-[#2C2C2C]">{doc.title}</h2>
        <a href={doc.path} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-[#2C2C2C]/55 hover:text-[#2C2C2C]" data-testid={`legal-doc-view-${id}`}>Voir la page <ExternalLink size={12} /></a>
      </div>
      <label className="mt-5 block text-sm font-medium text-[#2C2C2C]">Résumé affiché sur le site</label>
      <textarea rows={6} value={text} onChange={(e) => setText(e.target.value)} data-testid={`legal-doc-summary-${id}`} className="mt-1.5 w-full rounded-lg border border-[#2C2C2C]/20 px-3 py-2 text-sm outline-none focus:border-[#C9A84C]" />
      <p className="mt-5 text-sm font-medium text-[#2C2C2C]">Document complet (PDF)</p>
      {pdf ? (
        <div className="mt-2 flex flex-wrap items-center gap-3 rounded-xl bg-[#FAF6EF] p-3 text-sm" data-testid={`legal-doc-file-${id}`}>
          <FileText size={18} className="text-[#C9A84C]" />
          <a href={mediaUrl(pdf.url)} target="_blank" rel="noreferrer" className="flex-1 truncate text-[#2C2C2C] underline-offset-2 hover:underline" data-testid={`legal-doc-preview-${id}`}>{pdf.name}</a>
          <button onClick={() => setPdf(null)} disabled={busy} className="inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-700" data-testid={`legal-doc-remove-${id}`}><Trash2 size={13} /> Retirer</button>
        </div>
      ) : <p className="mt-2 text-xs text-[#2C2C2C]/50">Aucun PDF : le bouton de téléchargement n'apparaît pas sur le site.</p>}
      <div className="mt-5 flex flex-wrap gap-3">
        <label className={`${btn} cursor-pointer border border-[#C9A84C] text-[#2C2C2C] hover:bg-[#C9A84C] hover:text-white`} data-testid={`legal-doc-upload-${id}`}>
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} {pdf ? "Remplacer le PDF" : "Envoyer un PDF"}
          <input type="file" accept="application/pdf,.pdf" className="hidden" disabled={busy} data-testid={`legal-doc-file-input-${id}`} onChange={(e) => { upload(e.target.files[0]); e.target.value = ""; }} />
        </label>
        <button onClick={save} disabled={busy || !dirty} data-testid={`legal-doc-save-${id}`} className={`${btn} bg-[#2C2C2C] text-white hover:bg-[#C9A84C] hover:text-[#2C2C2C]`}>
          <Save size={15} /> Enregistrer
        </button>
      </div>
    </div>
  );
}

export default function AdminLegal() {
  const { content, refresh } = useContent();
  return (
    <div data-testid="admin-legal">
      <h1 className="font-serif text-3xl text-[#2C2C2C]">Documents légaux</h1>
      <p className="mt-1 text-sm text-[#2C2C2C]/60">Modifiez le résumé affiché sur chaque page et ajoutez le document complet en PDF. Pensez à cliquer sur « Enregistrer ».</p>
      <div className="mt-8 grid gap-6 lg:grid-cols-2">
        {LEGAL_DOCS.map((d) => (
          <DocCard key={d.key} doc={d} summary={content?.legal?.[d.key] || ""} file={content?.legal_files?.[d.key]} onSaved={refresh} />
        ))}
      </div>
    </div>
  );
}
